import React from 'react';
import {
  isPushSupported,
  isIosSafariTab,
  isIosDevice,
  getPermission,
  getExistingSubscription,
  fetchConfig,
  subscribe,
  unsubscribe,
} from '../../utils/pushNotifications';
import './notificationsToggle.css';

// "Push Notifications" row for the navbar user dropdown.
//
// The switch copies the Dark Mode row's INLINE styles rather than using classes
// from this component's stylesheet. `.user-dropdown-item` sets `display: block`,
// and CRA emits this component's CSS before navbar.css — same specificity, so
// navbar.css wins and a class-based `display: flex` here is silently overridden,
// stacking the label above the switch. Dark Mode escapes that only because
// inline styles beat any stylesheet. Keep these inline, and keep both rows in sync.
const ROW_STYLE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
};

const SWITCH_STYLE = (on) => ({
  position: 'relative',
  width: '36px',
  height: '20px',
  borderRadius: '999px',
  background: on ? '#2f81f7' : '#cbd5e1',
  transition: 'background 150ms ease',
  flexShrink: 0,
});

const KNOB_STYLE = (on) => ({
  position: 'absolute',
  top: '2px',
  left: on ? '18px' : '2px',
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  background: '#fff',
  boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
  transition: 'left 150ms ease',
});

function NotificationsToggle() {
  const [supported, setSupported] = React.useState(false);
  const [enabled, setEnabled] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [hint, setHint] = React.useState(null);
  const [blocked, setBlocked] = React.useState(false);
  const [iosTab, setIosTab] = React.useState(false);
  // Held from mount so the toggle has nothing to await before asking for
  // permission — see the ordering note in utils/pushNotifications.js.
  const configRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!isPushSupported()) return;

      if (isIosSafariTab()) {
        if (!cancelled) { setSupported(true); setIosTab(true); }
        return;
      }

      try {
        const config = await fetchConfig();
        if (cancelled) return;
        if (!config.enabled) return; // server has no VAPID keys; stay hidden

        configRef.current = config;
        setSupported(true);

        // Surface an existing block straight away rather than making the user
        // tap a toggle that cannot succeed.
        if (getPermission() === 'denied') { setBlocked(true); return; }

        const existing = await getExistingSubscription();
        if (!cancelled) setEnabled(!!existing);
      } catch (e) {
        // Config call failed (offline, or an older API without the endpoint).
        // Leave the row hidden rather than showing a broken control.
      }
    })();

    return () => { cancelled = true; };
  }, []);

  if (!supported) return null;

  if (iosTab) {
    return (
      <div className="notif-toggle-hint">
        <strong>To get notifications on iPhone</strong>
        <ol className="notif-steps">
          <li>Tap the Share button in Safari.</li>
          <li>Tap <strong>Add to Home Screen</strong>.</li>
          <li>Open Artemis from your Home Screen.</li>
          <li>Open this menu again and turn on Push Notifications.</li>
        </ol>
      </div>
    );
  }

  // Blocked is a dead end in the page: neither Safari nor Chrome will prompt
  // again, so the only way out is the OS/browser settings. Spell it out per
  // platform instead of saying "check your settings".
  if (blocked) {
    return (
      <div className="notif-toggle-hint error">
        <strong>Notifications are turned off for Artemis</strong>
        {isIosDevice() ? (
          <ol className="notif-steps">
            <li>Open the iPhone <strong>Settings</strong> app.</li>
            <li>Tap <strong>Notifications</strong>.</li>
            <li>Scroll to <strong>Artemis</strong> and tap it.</li>
            <li>Turn on <strong>Allow Notifications</strong>.</li>
            <li>Come back here and turn on Push Notifications.</li>
          </ol>
        ) : (
          <ol className="notif-steps">
            <li>Click the icon at the left of the address bar.</li>
            <li>Find <strong>Notifications</strong> and set it to <strong>Allow</strong>.</li>
            <li>Reload the page, then turn on Push Notifications.</li>
          </ol>
        )}
        <div className="notif-steps-note">
          If Artemis isn’t in that list: press and hold the Artemis icon on your Home
          Screen, remove it, then add it again from Safari and reopen it. That resets
          the setting.
        </div>
      </div>
    );
  }

  const handleToggle = async () => {
    setBusy(true);
    setHint(null);
    try {
      if (enabled) {
        await unsubscribe();
        setEnabled(false);
      } else {
        await subscribe(configRef.current);
        setEnabled(true);
      }
    } catch (e) {
      if (e && e.blocked) setBlocked(true);
      else setHint(e.message || 'Could not change notification settings.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        className="user-dropdown-item theme-toggle-row"
        onClick={handleToggle}
        aria-pressed={enabled}
        disabled={busy}
        style={ROW_STYLE}
      >
        <span>Push Notifications</span>
        <span className="theme-toggle-switch" role="presentation" style={SWITCH_STYLE(enabled)}>
          <span style={KNOB_STYLE(enabled)} />
        </span>
      </button>
      {hint && <div className="notif-toggle-hint error">{hint}</div>}
    </>
  );
}

export default NotificationsToggle;
